import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle, Star } from "lucide-react";
import { FORMS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/sneha-logo.jpg";

const Consultation = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", budget: "", property_type: "", timeline: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: "consultation" });
      setStatus("success");
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };

  const benefits = [
    "Personalized investment strategy for your goals",
    "Exclusive pre-launch and off-market opportunities",
    "Infrastructure impact analysis for every project",
    "ROI projections backed by real data",
    "Zero brokerage, zero hidden charges",
    "Post-investment portfolio tracking",
  ];

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left side */}
          <div>
            <img src={LOGO} alt="Sneha Reality" className="h-20 w-auto object-contain mb-8" />
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label">
              Private Advisory
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] mb-6">
              Book Your Private<br />
              <span className="text-gold-gradient italic">Advisory Session</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-[#A0A0A0] text-sm leading-relaxed mb-10">
              Our wealth architects will craft a personalized investment roadmap designed around your goals, budget, and timeline.
            </motion.p>

            <div className="space-y-4 mb-10">
              {benefits.map((b, i) => (
                <motion.div key={b}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.07 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle size={14} className="text-[#D4AF37] shrink-0" />
                  <span className="text-sm text-[#A0A0A0] font-light">{b}</span>
                </motion.div>
              ))}
            </div>

            {/* Trust badges */}
            <div className="glass-gold p-6">
              <div className="flex items-center gap-2 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-[#D4AF37] fill-[#D4AF37]" />)}
                <span className="text-xs text-[#666] ml-2">4.9/5 Client Rating</span>
              </div>
              <p className="text-xs text-[#666] italic">"Sneha Reality helped me identify the perfect entry point in Ulwe. The infrastructure analysis was extraordinary."</p>
              <p className="text-xs text-[#444] mt-2">— Rahul M., Investor, Dubai</p>
            </div>
          </div>

          {/* Form */}
          <div>
            {status === "success" ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="glass-gold p-12 text-center"
                data-testid={FORMS.successMsg}
              >
                <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-6" />
                <h2 className="font-display text-3xl text-[#FAF8F3] mb-3">Advisory Booked</h2>
                <p className="text-[#A0A0A0] text-sm mb-6">
                  Our wealth advisory team will contact you within 24 hours to schedule your private session.
                </p>
                <p className="text-xs text-[#666] mb-8">Expect a call from +91 7977009190</p>
                <Link to="/properties" className="btn-gold-solid btn-gold text-xs py-3 px-8">
                  Explore Properties <ArrowRight size={12} className="inline ml-2" />
                </Link>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                onSubmit={handleSubmit}
                className="glass-gold p-8 md:p-10 space-y-6"
              >
                <h2 className="font-display text-2xl text-[#FAF8F3]">Your Investment Profile</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Full Name *</label>
                    <input data-testid={FORMS.nameInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input-luxury" placeholder="Your full name" required />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Phone *</label>
                    <input data-testid={FORMS.phoneInput} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="input-luxury" placeholder="+91 XXXXX XXXXX" required />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Email</label>
                    <input data-testid={FORMS.emailInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      type="email" className="input-luxury" placeholder="your@email.com" />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Budget *</label>
                    <select data-testid={FORMS.budgetSelect} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      className="input-luxury bg-transparent" required>
                      <option value="" className="bg-[#111]">Select Budget</option>
                      <option value="Below 50L" className="bg-[#111]">Below ₹50 Lakh</option>
                      <option value="50L-1Cr" className="bg-[#111]">₹50L – ₹1 Cr</option>
                      <option value="1Cr-2Cr" className="bg-[#111]">₹1 Cr – ₹2 Cr</option>
                      <option value="2Cr-5Cr" className="bg-[#111]">₹2 Cr – ₹5 Cr</option>
                      <option value="5Cr+" className="bg-[#111]">₹5 Cr+</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Property Type</label>
                    <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                      className="input-luxury bg-transparent">
                      <option value="" className="bg-[#111]">Select Type</option>
                      <option value="Luxury Apartment" className="bg-[#111]">Luxury Apartment</option>
                      <option value="Villa" className="bg-[#111]">Villa</option>
                      <option value="Commercial" className="bg-[#111]">Commercial</option>
                      <option value="Plot" className="bg-[#111]">Plot / Land</option>
                      <option value="Any" className="bg-[#111]">Open to Suggestions</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Investment Horizon</label>
                    <select data-testid={FORMS.timelineSelect} value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                      className="input-luxury bg-transparent">
                      <option value="" className="bg-[#111]">Select Timeline</option>
                      <option value="1-2 Years" className="bg-[#111]">1–2 Years</option>
                      <option value="3-5 Years" className="bg-[#111]">3–5 Years</option>
                      <option value="5-8 Years" className="bg-[#111]">5–8 Years</option>
                      <option value="8+ Years" className="bg-[#111]">8+ Years</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">Additional Goals / Message</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="input-luxury resize-none h-24" placeholder="Tell us about your investment goals or any specific requirements..." rows={4} />
                </div>

                <button data-testid={FORMS.submitBtn} type="submit" disabled={loading}
                  className="btn-gold-solid btn-gold w-full justify-center py-4 text-sm">
                  {loading ? "Booking..." : "Request Private Advisory Session"}
                  {!loading && <ArrowRight size={14} className="inline ml-2" />}
                </button>
                {status === "error" && <p className="text-red-400 text-xs text-center">Something went wrong. Please call +91 7977009190</p>}
              </motion.form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Consultation;
